<template>
  <div class="animated fadeIn log-explorer">

    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0">Application Logs</h4>
        <small class="text-muted">
          Read in place from the application log folders — nothing is copied or stored elsewhere.
        </small>
      </b-col>
      <b-col cols="auto">
        <b-button size="sm" variant="outline-secondary" :to="'/application-logs/audit'" class="mr-2">
          <i class="fa fa-history mr-1"></i>Access trail
        </b-button>
        <b-button size="sm" variant="outline-secondary" :to="'/application-logs/sources'">
          <i class="fa fa-cog mr-1"></i>Sources
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <!-- ── Sources & files ─────────────────────────────────────────────── -->
      <b-col lg="3" class="mb-3">
        <b-card no-body class="shadow-sm h-100">
          <div class="p-2 border-bottom">
            <b-form-input v-model="fileFilter" size="sm" placeholder="Filter files…"
                          @input="debouncedFileFilter" />
          </div>
          <div class="src-list">
            <div v-if="loadingSources" class="p-3 text-center text-muted">
              <b-spinner small /> Loading sources…
            </div>
            <div v-for="s in sources" :key="s.code" class="src-group" :class="{ 'src-archived': s.archived }">
              <button type="button" class="src-head" @click="selectSource(s)">
                <span class="src-caret">{{ selected && selected.code === s.code ? '▾' : '▸' }}</span>
                <span class="src-name">{{ s.displayName }}</span>
                <b-badge v-if="s.timezone && s.timezone !== 'Asia/Dhaka'" variant="light" class="src-tag"
                         :title="'This source logs in ' + s.timezone + '; the rest of the box logs +06'">
                  {{ s.timezone === 'UTC' ? 'UTC' : s.timezone }}
                </b-badge>
                <b-badge v-if="s.archived" variant="light" class="src-tag" title="No longer being written to">
                  archived
                </b-badge>
              </button>

              <div v-if="selected && selected.code === s.code" class="src-files">
                <div v-if="loadingFiles" class="px-3 py-2 text-muted small"><b-spinner small /> Loading…</div>
                <p v-else-if="!files.length" class="px-3 py-2 text-muted small mb-0">No matching files.</p>
                <button v-for="f in files" :key="f.name" type="button"
                        class="src-file" :class="{ 'src-file-on': f.name === file, 'src-file-dim': !f.readable }"
                        :disabled="!f.readable"
                        :title="fileTitle(f)"
                        @click="selectFile(f)">
                  <span class="sf-dot" v-if="isLiveFile(f)" title="Being written now — click to follow live">●</span>
                  <span class="sf-name">{{ f.name }}</span>
                  <span class="sf-size">{{ humanSize(f.size) }}</span>
                </button>
                <div v-if="totalFiles > files.length" class="px-3 py-1 small text-muted">
                  showing {{ files.length }} of {{ totalFiles }}
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- ── Console ─────────────────────────────────────────────────────── -->
      <b-col lg="9" class="log-pane">
        <b-card no-body class="shadow-sm mb-2">
          <div class="filter-bar">
            <div class="fb-levels" v-if="availableLevels.length">
              <button v-for="lv in availableLevels" :key="lv" type="button"
                      class="fb-chip" :class="[levelChipClass(lv), { 'fb-on': levels.indexOf(lv) >= 0 }]"
                      :aria-pressed="levels.indexOf(lv) >= 0 ? 'true' : 'false'"
                      @click="toggleLevel(lv)">{{ lv }}</button>
            </div>

            <b-input-group size="sm" class="fb-search">
              <b-form-input v-model="query" placeholder="Search this file…  ( / )"
                            ref="searchInput" @keyup.enter="runSearch" />
              <b-input-group-append>
                <b-button :variant="regex ? 'primary' : 'outline-secondary'" @click="regex = !regex"
                          :title="regex ? 'Regular expression mode on' : 'Plain text search'">.*</b-button>
                <b-button variant="primary" @click="runSearch" :disabled="!file || loading">
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <b-form-input v-model="fromDate" type="date" size="sm" class="fb-date" title="From date" />
            <b-form-input v-model="toDate" type="date" size="sm" class="fb-date" title="To date" />

            <div class="fb-right">
              <b-button size="sm" :variant="following ? 'danger' : 'primary'" class="fb-live"
                        :disabled="!file" @click="toggleFollow"
                        :title="following ? 'Stop printing live' : 'Print new lines as they are written'">
                <span v-if="following" class="live-dot"></span>
                <i v-else class="fa fa-play"></i>
                {{ following ? 'LIVE — click to stop' : 'Go Live' }}
              </b-button>
              <b-button size="sm" :variant="wrap ? 'primary' : 'outline-secondary'" @click="toggleWrap"
                        title="Wrap long lines (caps the buffer at 2,000 lines)">Wrap</b-button>
              <b-dropdown size="sm" variant="outline-secondary" right text="Download" :disabled="!file">
                <b-dropdown-item @click="downloadFiltered">
                  Filtered slice (.gz)
                  <div class="small text-muted">current filters, secrets masked</div>
                </b-dropdown-item>
                <b-dropdown-item v-if="canDownloadRaw" @click="confirmRawDownload">
                  Raw whole file
                  <div class="small text-muted">unmasked · audited</div>
                </b-dropdown-item>
                <b-dropdown-text v-else class="small text-muted" style="max-width:230px">
                  Raw download is disabled for this source.
                </b-dropdown-text>
              </b-dropdown>
              <b-button size="sm" variant="outline-secondary" @click="reload" :disabled="!file" title="Reload tail">
                <i class="fa fa-refresh"></i>
              </b-button>
            </div>
          </div>
        </b-card>

        <log-console
          ref="console"
          :entries="visibleEntries"
          :loading="loading"
          :wrap="wrap"
          :query="highlightQuery"
          :new-count="newCount"
          :following="following"
          :empty-text="emptyText"
          :empty-hint="emptyHint"
          @jump-latest="jumpToLatest"
          @at-bottom-change="onAtBottomChange" />

        <div class="status-bar">
          <span v-if="file"><strong>{{ file }}</strong></span>
          <span v-if="meta.format" class="sb-chip">{{ meta.format }}</span>
          <span v-if="meta.timezone" class="sb-chip">{{ meta.timezone }}</span>
          <span v-if="meta.fileSize">{{ humanSize(meta.fileSize) }}</span>
          <span v-if="mode === 'search'">{{ matched }} match<span v-if="matched !== 1">es</span></span>
          <span v-else>{{ entries.length }} entr<span v-if="entries.length !== 1">ies</span></span>
          <span v-if="wrap && entries.length >= wrapCap" class="sb-warn">
            wrap mode caps the buffer at {{ wrapCap }} lines
          </span>
          <span v-if="following" class="sb-live">
            <span class="live-dot"></span>LIVE
            <span class="sb-live-age">· {{ liveAgeLabel }}</span>
          </span>
          <span class="sb-spacer"></span>
          <b-button v-if="truncated" size="sm" variant="outline-warning" @click="continueScan">
            Truncated — scanned {{ humanSize(bytesScanned) }} · continue
          </b-button>
          <span v-if="rotated" class="sb-warn">log rotated — restarted from the top</span>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import logService from '@/services/logService'
import toastService from '@/services/toastService'
import LogConsole from './components/LogConsole'

var WRAP_CAP = 2000
// Adaptive follow cadence. A flat 2s interval made a busy log arrive in visible clumps.
// Poll fast while lines are flowing, back off geometrically when the file goes quiet — on a
// bursty log that is both snappier when it matters and cheaper than a flat interval when it
// does not. Every non-empty poll writes an audit row, which is the real cost of going faster.
var POLL_FAST_MS = 700
var POLL_SLOW_MS = 3000
// How recently a file must have been written to count as live. Generous on purpose: a
// false positive just means empty polls and a truthful "quiet for Ns", whereas a false
// negative silently refuses to follow a log that is in fact moving.
var LIVE_WINDOW_MS = 15 * 60 * 1000

export default {
  name: 'LogExplorer',
  components: { 'log-console': LogConsole },
  data: function() {
    return {
      sources: [],
      selected: null,
      files: [],
      totalFiles: 0,
      file: '',
      fileFilter: '',
      // Name of the file currently being written, learned from an unfiltered listing.
      // Not files[0]: the listing is filtered and paged server-side, so index 0 is only
      // the newest file when no filter is applied.
      pollDelay: 700,
      // Heartbeat. This log arrives in bursts — 18 lines at once, then nothing for
      // 10s — so "following" needs to prove it is alive during the quiet stretches.
      lastLineAt: null,
      nowTick: 0,
      tickTimer: null,

      entries: [],
      meta: {},
      mode: 'tail',
      matched: 0,
      truncated: false,
      rotated: false,
      bytesScanned: 0,
      nextOffset: 0,

      levels: [],
      query: '',
      highlightQuery: '',
      regex: false,
      fromDate: '',
      toDate: '',

      wrap: false,
      wrapCap: WRAP_CAP,
      following: false,
      newCount: 0,
      atBottom: true,

      loading: false,
      loadingSources: false,
      loadingFiles: false,

      seq: 0,
      pollTimer: null,
      searchDebounce: null,
      fileDebounce: null,
      cancelSource: null
    }
  },
  computed: {
    liveAgeLabel: function() {
      if (!this.lastLineAt) { return 'waiting for the first new line' }
      var secs = Math.max(0, Math.round((this.nowTick - this.lastLineAt) / 1000))
      if (secs <= 2) { return 'new lines just now' }
      return 'quiet for ' + secs + 's'
    },
    availableLevels: function() {
      return (this.meta.levels && this.meta.levels.length) ? this.meta.levels : []
    },
    canDownloadRaw: function() {
      return !!(this.selected && this.selected.canDownloadRaw)
    },
    // Wrap mode is not virtualised, so the buffer must be bounded here.
    visibleEntries: function() {
      if (!this.wrap || this.entries.length <= WRAP_CAP) { return this.entries }
      return this.entries.slice(this.entries.length - WRAP_CAP)
    },
    emptyText: function() {
      if (!this.file) { return 'Pick a log file on the left to begin.' }
      if (this.meta.binary) { return 'This file is binary and cannot be shown as text.' }
      if (this.mode === 'search') { return 'No lines matched.' }
      return 'This file is empty.'
    },
    emptyHint: function() {
      if (this.meta.binary) { return 'Kafka partition segments are named *.log but hold binary records.' }
      if (this.mode === 'search' && this.truncated) {
        return 'The scan hit its byte cap before reaching the end — use “continue”.'
      }
      return ''
    }
  },
  created: function() {
    this.loadSources()
  },
  mounted: function() {
    document.addEventListener('keydown', this.onKey)
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeDestroy: function() {
    this.stopPolling()
    document.removeEventListener('keydown', this.onKey)
    document.removeEventListener('visibilitychange', this.onVisibility)
    if (this.cancelSource) { this.cancelSource.cancel('leaving') }
  },
  methods: {
    // ── loading ──────────────────────────────────────────────────────────
    loadSources: function() {
      var self = this
      self.loadingSources = true
      logService.getSources(function(data) {
        self.sources = data.sources || []
        self.loadingSources = false
        if (self.sources.length) { self.selectSource(self.sources[0]) }
      }, function(msg) {
        self.loadingSources = false
        toastService.showErrorToast(msg)
      })
    },

    selectSource: function(source) {
      if (this.selected && this.selected.code === source.code) {
        this.selected = null
        return
      }
      this.selected = source
      this.file = ''
      this.entries = []
      this.meta = {}
      this.stopPolling()
      this.loadFiles()
    },

    loadFiles: function() {
      var self = this
      if (!self.selected) { return }
      self.loadingFiles = true
      logService.getFiles(self.selected.code,
        { q: self.fileFilter || undefined, size: 200 },
        function(data) {
          self.files = data.files || []
          self.totalFiles = data.total || 0
          self.loadingFiles = false
          if (self.files.length && !self.file) { self.selectFile(self.files[0]) }
        },
        function(msg) { self.loadingFiles = false; toastService.showErrorToast(msg) })
    },

    debouncedFileFilter: function() {
      var self = this
      clearTimeout(self.fileDebounce)
      self.fileDebounce = setTimeout(function() { self.loadFiles() }, 350)
    },

    /**
     * The file being written right now.
     *
     * Deliberately NOT keyed off source.archived: that is a hand-maintained flag and it is
     * currently set on client-api, whose log was last written one second ago. A flag someone
     * can get wrong must not be able to disable live tail.
     *
     * `active` is the server's own "newest file" marker (LogViewerService: first row of the
     * first page), so it is computed after the name filter — under a filter it can land on a
     * rotated file. The mtime check is what makes that harmless: a rolled file's mtime is
     * hours old, so it fails recency even when it is wrongly marked active.
     */
    isLiveFile: function(f) {
      if (!f || !f.active || !f.lastModified) { return false }
      return (Date.now() - f.lastModified) < LIVE_WINDOW_MS
    },

    fileTitle: function(f) {
      if (!f.readable) { return f.name + ' — not readable by the API process' }
      return this.isLiveFile(f) ? f.name + ' — click to follow live' : f.name
    },

    selectFile: function(f) {
      if (!f.readable) { return }
      this.file = f.name
      this.mode = 'tail'
      this.query = ''
      this.highlightQuery = ''
      this.stopPolling()
      // Follow is armed here but only engages once loadTail lands: nextOffset comes from
      // applyWindow, and polling from a stale cursor would replay lines already on screen.
      this.loadTail(this.isLiveFile(f))
    },

    /** Cancel whatever is in flight: a slow scan must never overwrite a newer view. */
    freshRequest: function() {
      if (this.cancelSource) { this.cancelSource.cancel('superseded') }
      this.cancelSource = logService.newCancelSource()
      this.seq += 1
      return { token: this.cancelSource.token, seq: this.seq }
    },

    /** @param follow start following once the cursor is known. Default off: every other
     *                 caller (toggleFollow, runSearch, reload, the rotation branch of poll)
     *                 either manages `following` itself or must not turn it on. */
    loadTail: function(follow) {
      var self = this
      if (!self.file) { return }
      var req = self.freshRequest()
      self.loading = true
      logService.tail(self.selected.code, { file: self.file, lines: 500 },
        function(data) {
          if (req.seq !== self.seq) { return }
          self.applyWindow(data, true)
          self.loading = false
          self.$nextTick(function() { self.$refs.console && self.$refs.console.scrollToBottom() })
          if (follow) {
            self.following = true
            self.newCount = 0
            self.startPolling()
          }
        },
        function(msg) {
          if (req.seq !== self.seq) { return }
          self.loading = false
          toastService.showErrorToast(msg)
        }, req.token)
    },

    applyWindow: function(data, replace) {
      this.meta = {
        format: data.format, levels: data.levels, timezone: data.timezone,
        fileSize: data.fileSize, binary: !!data.binary
      }
      if (data.binary) {
        this.entries = []
        return
      }
      var incoming = this.decorate(data.entries || [], replace ? 0 : this.entries.length)
      // Object.freeze skips Vue 2's deep reactivity walk over thousands of objects.
      this.entries = replace ? Object.freeze(incoming)
                             : Object.freeze(this.entries.concat(incoming))
      this.mode = 'tail'
      this.matched = 0
      this.truncated = !!data.truncated
      this.rotated = !!data.rotated
      this.bytesScanned = data.bytesScanned || 0
      this.nextOffset = data.nextOffset || 0
    },

    decorate: function(list, startAt) {
      var out = []
      for (var i = 0; i < list.length; i++) {
        var e = list[i]
        e._key = e.offset + ':' + (startAt + i)
        e._n = startAt + i + 1
        e.continuation = e.continuation || []
        out.push(e)
      }
      return out
    },

    // ── search ───────────────────────────────────────────────────────────
    runSearch: function() {
      var self = this
      if (!self.file) { return }
      if (!self.query || !self.query.trim()) { self.loadTail(); return }
      self.stopPolling()
      var req = self.freshRequest()
      self.loading = true
      self.highlightQuery = self.regex ? '' : self.query
      logService.search(self.selected.code, {
        file: self.file,
        q: self.query,
        regex: self.regex,
        levels: self.levels.length ? self.levels.join(',') : undefined,
        from: self.fromDate || undefined,
        to: self.toDate || undefined
      }, function(data) {
        if (req.seq !== self.seq) { return }
        self.meta = {
          format: data.format, levels: data.levels, timezone: data.timezone,
          fileSize: data.fileSize, binary: !!data.binary
        }
        self.entries = Object.freeze(self.decorate(data.entries || [], 0))
        self.mode = 'search'
        self.matched = data.matched || 0
        self.truncated = !!data.truncated
        self.bytesScanned = data.bytesScanned || 0
        self.nextOffset = data.nextOffset || 0
        self.loading = false
        self.$nextTick(function() { self.$refs.console && self.$refs.console.scrollToTop() })
      }, function(msg) {
        if (req.seq !== self.seq) { return }
        self.loading = false
        toastService.showErrorToast(msg)
      }, req.token)
    },

    continueScan: function() {
      var self = this
      if (self.mode !== 'search') { self.loadTail(); return }
      var req = self.freshRequest()
      self.loading = true
      logService.search(self.selected.code, {
        file: self.file, q: self.query, regex: self.regex,
        fromOffset: self.nextOffset,
        levels: self.levels.length ? self.levels.join(',') : undefined,
        from: self.fromDate || undefined, to: self.toDate || undefined
      }, function(data) {
        if (req.seq !== self.seq) { return }
        var more = self.decorate(data.entries || [], self.entries.length)
        self.entries = Object.freeze(self.entries.concat(more))
        self.matched += data.matched || 0
        self.truncated = !!data.truncated
        self.bytesScanned += data.bytesScanned || 0
        self.nextOffset = data.nextOffset || 0
        self.loading = false
      }, function(msg) {
        if (req.seq !== self.seq) { return }
        self.loading = false
        toastService.showErrorToast(msg)
      }, req.token)
    },

    // ── follow ───────────────────────────────────────────────────────────
    toggleFollow: function() {
      if (this.following) { this.stopPolling(); return }
      if (this.mode === 'search') { this.query = ''; this.loadTail() }
      this.following = true
      this.newCount = 0
      this.startPolling()
    },

    startPolling: function() {
      var self = this
      self.stopTimerOnly()
      self.nowTick = Date.now()
      self.pollDelay = POLL_FAST_MS
      self.armPoll()
      // Separate 1s ticker: the age label has to advance between polls, and during a
      // burst-free stretch there is no poll result to drive a re-render.
      self.tickTimer = setInterval(function() { self.nowTick = Date.now() }, 1000)
    },

    /**
     * Chain the next poll. Every exit path in poll() must call this or the chain dies
     * silently — that is the trade for adaptive spacing over a self-repeating interval.
     */
    armPoll: function() {
      var self = this
      if (!self.following) { return }
      if (self.pollTimer) { clearTimeout(self.pollTimer) }
      self.pollTimer = setTimeout(function() { self.poll() }, self.pollDelay)
    },

    stopTimerOnly: function() {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null }
      if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null }
    },

    stopPolling: function() {
      this.stopTimerOnly()
      this.following = false
      this.newCount = 0
      this.lastLineAt = null
    },

    /** Pause while the tab is hidden — a background tab polling every 2s is pure waste. */
    onVisibility: function() {
      if (!this.following) { return }
      if (document.hidden) { this.stopTimerOnly() } else { this.startPolling() }
    },

    poll: function() {
      var self = this
      // A tail/search in flight owns the cursor; skip this turn but keep the chain alive.
      if (!self.file || self.loading) { self.armPoll(); return }
      logService.content(self.selected.code,
        { file: self.file, fromOffset: self.nextOffset, maxLines: 2000 },
        function(data) {
          if (data.rotated) {
            toastService.showSuccessToast('Log rotated — reloading from the new file')
            self.rotated = true
            self.nextOffset = 0
            self.loadTail()
            self.pollDelay = POLL_FAST_MS
            self.armPoll()
            return
          }
          var incoming = data.entries || []
          if (!incoming.length) {
            self.nextOffset = data.nextOffset || self.nextOffset
            // Quiet: ease off, so an idle log is cheaper than the old flat interval.
            self.pollDelay = Math.min(self.pollDelay * 2, POLL_SLOW_MS)
            self.armPoll()
            return
          }
          self.pollDelay = POLL_FAST_MS
          self.lastLineAt = Date.now()
          var more = self.decorate(incoming, self.entries.length)
          self.entries = Object.freeze(self.entries.concat(more))
          self.nextOffset = data.nextOffset || self.nextOffset
          self.meta.fileSize = data.fileSize
          if (self.atBottom) {
            self.$nextTick(function() { self.$refs.console && self.$refs.console.scrollToBottom() })
          } else {
            // Never yank the viewport out from under someone who is reading.
            self.newCount += more.length
          }
          self.armPoll()
        },
        function(msg) {
          // A blip must not spin forever; stop and tell the user once.
          self.stopPolling()
          toastService.showErrorToast(msg)
        })
    },

    jumpToLatest: function() {
      this.newCount = 0
      this.$refs.console && this.$refs.console.scrollToBottom()
    },

    onAtBottomChange: function(atBottom) {
      this.atBottom = atBottom
      if (atBottom) { this.newCount = 0 }
    },

    // ── filters ──────────────────────────────────────────────────────────
    toggleLevel: function(level) {
      var i = this.levels.indexOf(level)
      if (i >= 0) { this.levels.splice(i, 1) } else { this.levels.push(level) }
      if (this.mode === 'search') { this.runSearch() }
    },

    levelChipClass: function(level) {
      var l = level.toUpperCase()
      if (l === 'ERROR' || l === 'FATAL' || l === 'CRITICAL') { return 'chip-e' }
      if (l === 'WARN' || l === 'WARNING') { return 'chip-w' }
      if (l === 'INFO') { return 'chip-i' }
      return 'chip-d'
    },

    toggleWrap: function() { this.wrap = !this.wrap },

    reload: function() {
      this.stopPolling()
      if (this.mode === 'search') { this.runSearch() } else { this.loadTail() }
    },

    // ── download ─────────────────────────────────────────────────────────
    downloadFiltered: function() {
      var self = this
      logService.download(self.selected.code, {
        file: self.file,
        mode: 'filtered',
        q: self.query || undefined,
        regex: self.regex,
        levels: self.levels.length ? self.levels.join(',') : undefined,
        from: self.fromDate || undefined,
        to: self.toDate || undefined
      }, self.file + '.gz',
        function() { toastService.showSuccessToast('Filtered log downloaded') },
        function(msg) { toastService.showErrorToast(msg) })
    },

    confirmRawDownload: function() {
      var self = this
      var size = self.humanSize(self.meta.fileSize || 0)
      self.$bvModal.msgBoxConfirm(
        'Download the entire file (' + size + ') with no redaction applied? This is recorded in the access trail.',
        { title: 'Raw download', okTitle: 'Download', okVariant: 'warning', cancelTitle: 'Cancel' }
      ).then(function(ok) {
        if (!ok) { return }
        logService.download(self.selected.code, { file: self.file, mode: 'raw' }, self.file,
          function() { toastService.showSuccessToast('Raw file downloaded') },
          function(msg) { toastService.showErrorToast(msg) })
      })
    },

    // ── keyboard ─────────────────────────────────────────────────────────
    onKey: function(e) {
      var tag = (e.target && e.target.tagName) || ''
      var typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      if (e.key === '/' && !typing) {
        e.preventDefault()
        this.$refs.searchInput && this.$refs.searchInput.focus()
        return
      }
      if (typing) {
        if (e.key === 'Escape') { e.target.blur() }
        return
      }
      if (e.key === 'f') { this.toggleFollow() }
      else if (e.key === 'g') { this.$refs.console && this.$refs.console.scrollToTop() }
      else if (e.key === 'G') { this.jumpToLatest() }
      else if (e.key === 'Escape') { this.query = ''; this.highlightQuery = ''; this.loadTail() }
    },

    // ── formatting ───────────────────────────────────────────────────────
    humanSize: function(bytes) {
      if (!bytes && bytes !== 0) { return '' }
      var units = ['B', 'KB', 'MB', 'GB', 'TB']
      var i = 0
      var n = bytes
      while (n >= 1024 && i < units.length - 1) { n = n / 1024; i++ }
      return (i === 0 ? n : n.toFixed(1)) + ' ' + units[i]
    }
  }
}
</script>

<style scoped>
/*
 * One-screen layout: the console scrolls, the page does not.
 *
 * The console already had `flex: 1 1 auto; overflow-y: auto` on its body, but nothing ever
 * gave it a bounded height — only `min-height: 320px` — so it grew to fit every line and the
 * document scrolled instead. Bounding the pane is what makes its internal scrollbar real.
 *
 * Only the page chrome above the row is a fixed subtraction (CoreUI header + breadcrumb +
 * the title row). The filter bar and status bar keep their natural height and the console
 * absorbs whatever is left, so neither needs to be measured here.
 */
.log-pane {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 400px;
}
/* min-height:0 is load-bearing: without it this flex child refuses to shrink below its
   content and the overflow lands on the page again. */
.log-pane >>> .log-console {
  flex: 1 1 auto;
  min-height: 0;
}
.log-pane .status-bar { flex: 0 0 auto; }

/* Keep the file list inside the same envelope so it cannot stretch the page either. */
.src-list { max-height: calc(100vh - 290px); min-height: 220px; overflow-y: auto; }

.src-group { border-bottom: 1px solid #f0f3f5; }
.src-archived { opacity: 0.62; }

.src-head {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: none;
  border: 0;
  text-align: left;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #23282c;
  cursor: pointer;
}
.src-head:hover { background: #f0f3f5; }
.src-caret { color: #8f9ba6; width: 10px; }
.src-name { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.src-tag { font-size: 9.5px; font-weight: 600; letter-spacing: 0.04em; border: 1px solid #c8ced3; }

.src-files { background: #fbfcfd; padding-bottom: 4px; }
.src-file {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: none;
  border: 0;
  text-align: left;
  padding: 4px 12px 4px 26px;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 11.5px;
  color: #3e515b;
  cursor: pointer;
}
.src-file:hover { background: #eef3f6; }
.src-file-on { background: #e1f0f5; color: #094c61; font-weight: 600; }
.src-file-dim { color: #a4b7c1; cursor: not-allowed; }
.sf-dot { color: #1f6b4a; font-size: 8px; }
.sf-name { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sf-size { color: #8f9ba6; font-variant-numeric: tabular-nums; }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
}
.fb-levels { display: flex; gap: 4px; }
.fb-chip {
  border: 1px solid #c8ced3;
  background: #fff;
  border-radius: 3px;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #73818f;
  cursor: pointer;
}
.fb-chip:hover { border-color: #8f9ba6; }
.fb-on { color: #fff; }
.chip-e.fb-on { background: #b22d22; border-color: #b22d22; }
.chip-w.fb-on { background: #9a6206; border-color: #9a6206; }
.chip-i.fb-on { background: #2c5f86; border-color: #2c5f86; }
.chip-d.fb-on { background: #62757b; border-color: #62757b; }

.fb-search { flex: 1 1 240px; max-width: 420px; }
.fb-date { flex: 0 0 140px; }
.fb-right { display: flex; gap: 6px; margin-left: auto; align-items: center; }

.status-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 4px 0;
  font-size: 11.5px;
  color: #73818f;
  font-family: ui-monospace, Menlo, Consolas, monospace;
}
.sb-chip { border: 1px solid #dce4e7; border-radius: 3px; padding: 1px 7px; }

.fb-live { font-weight: 600; letter-spacing: 0.2px; }
.sb-live { color: #c8281e; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
.sb-live-age { color: #73818f; font-weight: 400; }

/* Pulses whether or not lines are arriving — that is the point: it says the poll
   is alive while the log is between bursts. */
.live-dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: currentColor; margin-right: 2px;
  animation: live-pulse 1.4s ease-in-out infinite;
}
.fb-live .live-dot { background: #fff; }
@keyframes live-pulse {
  0%, 100% { opacity: 1;   transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.72); }
}
@media (prefers-reduced-motion: reduce) {
  .live-dot { animation: none; }
}
.sb-spacer { flex: 1 1 auto; }
.sb-warn { color: #9a6206; }

@media (max-width: 991px) {
  /* Stacked columns: a viewport-height pane per column would push the console off-screen. */
  .log-pane { height: auto; }
  .log-pane >>> .log-console { height: 60vh; }
  .src-list { max-height: 260px; min-height: 0; }
  .fb-right { margin-left: 0; }
}
</style>
